/**
 * copyText — verbatim clipboard copy with a testable seam (Tasks 070 + 085).
 *
 * Prefers the async Clipboard API; falls back to a hidden-textarea +
 * document.execCommand when the API is unavailable (non-secure context / older
 * browsers). The text is copied VERBATIM — no transformation — so line breaks
 * and whitespace are preserved exactly.
 *
 * The clipboard writer and the fallback are INJECTABLE so both the success and
 * failure paths can be verified hermetically, without real clipboard permission
 * or a browser (see copy-text.smoke.ts — this closes the Task 070 gap where
 * headless automation could not exercise a trusted clipboard write). In
 * production nothing is passed and the real defaults are used.
 */
export interface CopyTextDeps {
  /**
   * Async clipboard writer. Defaults to navigator.clipboard.writeText when
   * available. Pass a fake to exercise success/failure; pass null to force the
   * fallback path.
   */
  writeText?: ((text: string) => Promise<void>) | null;
  /** Synchronous fallback used when writeText is unavailable. */
  fallbackCopy?: (text: string) => boolean;
}

function resolveClipboardWrite(): ((text: string) => Promise<void>) | null {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return (text: string) => navigator.clipboard.writeText(text);
  }
  return null;
}

function execCommandCopy(text: string): boolean {
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export async function copyText(text: string, deps: CopyTextDeps = {}): Promise<boolean> {
  const writeText =
    deps.writeText !== undefined ? deps.writeText : resolveClipboardWrite();
  if (writeText) {
    try {
      await writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  const fallbackCopy = deps.fallbackCopy ?? execCommandCopy;
  return fallbackCopy(text);
}
